/**
 * Rúbrica de calificación.
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `Escala` desde 2026-07. El JSON
 * `criterio` es POLIMÓRFICO según el tipo de información — documental,
 * cuantitativa y opinión tienen formas incompatibles entre sí.
 *
 * Contrato tipado de una respuesta del API. Espeja la entidad del backend; no es
 * un modelo del frontend ni debe llevar lógica.
 */

export type TipoInformacion = 'documental' | 'cuantitativa' | 'opinion'

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface NivelCriterio {
  codigo?: string
  label?: string
  grado?: string
  tendencia?: string
  rango_min: number
  rango_max: number
  descripcion?: string
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface GradoDocumental {
  grado: string
  rango_min: number
  rango_max: number
  descriptores: Record<string, string>
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface RubricaDocumental {
  criterios: string[]
  grados: GradoDocumental[]
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface RubricaCuantitativa {
  niveles: NivelCriterio[]
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface MuestraOpinion {
  grupo_interes: string
  tipo_muestreo: string
  criterios?: string
  tecnica: string
}

/** Fila de la tabla de valoración del MAAM: % de cumplimiento → banda 0–5. */
export interface GradoValoracionOpinion {
  grado: string
  grado_valoracion?: string
  valoracion?: string
  rango_min: number
  rango_max: number
  pct_min?: number | null
  pct_max?: number | null
  juicio_de_valor?: string
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface RubricaOpinion {
  /** Debe venir al menos uno de muestreo o grados (espejo del backend). */
  muestreo?: MuestraOpinion[]
  grados?: GradoValoracionOpinion[]
  conversion: {
    formula: string
    escala: string
  }
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export type RubricaCriterio = RubricaDocumental | RubricaCuantitativa | RubricaOpinion

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface CriterioEvaluacion {
  id?: number
  criterio: RubricaCriterio
  id_tipo_componente: number
  tipo_informacion: TipoInformacion
  /** C-01: versión del modelo a la que pertenece el criterio (trazabilidad). */
  id_version?: number | null
  created_at?: string
  updated_at?: string
}

// ─── Type guards ─────────────────────────────────────────────────────
// Ojo con el orden de los discriminantes: desde que la rúbrica de opinión
// admite `grados` (tabla de valoración), `grados` ya NO distingue por sí solo
// a la documental. Se discrimina por el campo exclusivo de cada forma:
// documental = criterios + grados · cuantitativa = niveles · opinion = conversion.
export function esRubricaOpinion(r: RubricaCriterio): r is RubricaOpinion {
  return typeof (r as RubricaOpinion)?.conversion === 'object' && (r as RubricaOpinion).conversion !== null
}

/**
 * Type guard de rúbrica DOCUMENTAL (matriz [grado × criterio]).
 *
 * Descarta primero la de opinión porque las formas se solapan: una rúbrica de
 * opinión también puede traer `grados`. El orden de comprobación es, por tanto,
 * significativo — invertirlo clasificaría rúbricas de opinión como documentales.
 */
export function esRubricaDocumental(r: RubricaCriterio): r is RubricaDocumental {
  return !esRubricaOpinion(r)
    && Array.isArray((r as RubricaDocumental).criterios)
    && Array.isArray((r as RubricaDocumental).grados)
}

/**
 * Type guard de rúbrica CUANTITATIVA: se identifica por tener `niveles`.
 *
 * Es el discriminador más simple de los tres porque `niveles` no aparece en las
 * otras dos formas.
 */
export function esRubricaCuantitativa(r: RubricaCriterio): r is RubricaCuantitativa {
  return Array.isArray((r as RubricaCuantitativa).niveles)
}

// ─── Escalas de valoración (tabla `grados`) ──────────────────────────
// No confundir con la rúbrica: la rúbrica describe QUÉ se evalúa; la escala de
// valoración traduce la nota obtenida a un nivel de cumplimiento. Cada modelo
// puede tener la suya (los históricos usan la CNA 1–10); si no la tiene, se usa
// el catálogo por defecto del MAAM (0–5), que llega con `id_modelo: null`.

export interface GradoValoracion {
  id_grado?: number
  id_modelo: number | null
  orden: number
  nombre: string
  rango_min: number
  rango_max: number
}

/** Forma con la que el API de criterio evaluacion devuelve este dato. */
export interface EscalaValoracion {
  id_modelo: number | null
  modelo: string | null
  escala_max: number
  grados: GradoValoracion[]
}
